import type { SQL } from "drizzle-orm";
import {
  and,
  asc,
  count,
  countDistinct,
  desc,
  eq,
  exists,
  getTableColumns,
  ilike,
  inArray,
  isNotNull,
  isNull,
  ne,
  or,
  sql,
} from "drizzle-orm";

import { db } from "@/db/db";
import {
  account,
  directWsInvitation,
  feed,
  feedGroup,
  feedGroupMembership,
  feedPreference,
  file,
  item,
  permission,
  tag,
  transcription,
  workspaceCommandAlias,
  workspaceMembership,
  workspaceMembershipTag,
  wsMembershipAvailability,
} from "@/db/schema";
import { emptyQuery } from "@/models/emptyQuery";
import { logQuery } from "@/models/logQuery";
import { sqlMembershipIsOffline, sqlMembershipIsOnline } from "@/models/repositories/membershipOfflineSql";
import {
  DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL,
  DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL,
  type DriverFilterConfig,
  type DriverSearchColumn,
  type DriverSearchTarget,
  type DriverSortKey,
  type DriverStatusFilter,
  type DriverViewTab,
} from "@/types/driver";

function aliasSubquery() {
  return db
    .selectDistinctOn([workspaceCommandAlias.feedId], {
      feedId: workspaceCommandAlias.feedId,
      alias: workspaceCommandAlias.alias,
    })
    .from(workspaceCommandAlias)
    .orderBy(workspaceCommandAlias.feedId)
    .as("latestItemAlias");
}

type LatestItemAliasSubquery = ReturnType<typeof aliasSubquery>;

function latestInvitationSubquery() {
  return db
    .selectDistinctOn([directWsInvitation.workspaceMembershipId], {
      workspaceMembershipId: directWsInvitation.workspaceMembershipId,
      email: directWsInvitation.email,
      phoneNumber: directWsInvitation.phoneNumber,
      name: directWsInvitation.name,
    })
    .from(directWsInvitation)
    .orderBy(directWsInvitation.workspaceMembershipId, desc(directWsInvitation.createdAt))
    .as("latestInvitation");
}

/** One alias feed per membership (invited-but-not-logged-in users may have none synced locally) */
function primaryAliasFeedSubquery() {
  return db
    .selectDistinctOn([feed.aliasMembershipId], getTableColumns(feed))
    .from(feed)
    .where(eq(feed.isAliasFeed, true))
    .orderBy(feed.aliasMembershipId, feed.id)
    .as("aliasFeed");
}

function statusFilterToCondition(filter: DriverStatusFilter, unactivatedMembershipIds: string[]): SQL | undefined {
  switch (filter) {
    case "driving":
      return and(eq(wsMembershipAvailability.calculatedAvailability, "present"), sqlMembershipIsOnline());
    case "not-driving":
      return and(
        ne(wsMembershipAvailability.calculatedAvailability, "present"),
        sqlMembershipIsOnline(),
        or(
          eq(wsMembershipAvailability.calculatedAvailability, "unknown"),
          and(
            eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
            or(
              ne(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false),
              isNull(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled),
            ),
            sql`NOT (
              ${wsMembershipAvailability.mostRecentHandsFreeStatusEnabled} IS TRUE
              AND ${wsMembershipAvailability.mostRecentVolumeAudible} IS FALSE
            )`,
          ),
        ),
      );
    case "hands-free-off":
      return and(
        eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
        sqlMembershipIsOnline(),
        eq(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, false),
      );
    case "volume-muted":
      return and(
        eq(wsMembershipAvailability.calculatedAvailability, "notPresent"),
        sqlMembershipIsOnline(),
        eq(wsMembershipAvailability.mostRecentHandsFreeStatusEnabled, true),
        eq(wsMembershipAvailability.mostRecentVolumeAudible, false),
      );
    case "offline":
      return sqlMembershipIsOffline();
    case "never-logged-in":
      if (unactivatedMembershipIds.length === 0) return sql`false`;
      return inArray(workspaceMembership.id, unactivatedMembershipIds);
  }
}

function isOnlyNeverLoggedInFilter(config: DriverFilterConfig): boolean {
  return config.statusFilter.length === 1 && config.statusFilter[0] === "never-logged-in";
}

function tabTeamAndUnactivatedFilterConditions(
  tab: DriverViewTab,
  teamAliases: string[],
  onlyNeverLoggedIn: boolean,
  unactivatedMembershipIds: string[],
  latestItemAlias: LatestItemAliasSubquery,
): SQL[] {
  const conditions: SQL[] = [];
  if (tab === "my") {
    if (!onlyNeverLoggedIn) {
      conditions.push(isNotNull(feedPreference.id));
    }
    if (teamAliases.length > 0) {
      if (onlyNeverLoggedIn && unactivatedMembershipIds.length > 0) {
        conditions.push(
          or(inArray(latestItemAlias.alias, teamAliases), inArray(workspaceMembership.id, unactivatedMembershipIds)),
        );
      } else {
        conditions.push(inArray(latestItemAlias.alias, teamAliases));
      }
    }
  }
  if (tab === "all" && teamAliases.length > 0) {
    if (onlyNeverLoggedIn && unactivatedMembershipIds.length > 0) {
      conditions.push(
        or(inArray(latestItemAlias.alias, teamAliases), inArray(workspaceMembership.id, unactivatedMembershipIds)),
      );
    } else {
      conditions.push(inArray(latestItemAlias.alias, teamAliases));
    }
  }
  return conditions;
}

function buildFilterConditions(
  config: DriverFilterConfig,
  refs: {
    latestItemAlias: LatestItemAliasSubquery;
    latestInvitation: ReturnType<typeof latestInvitationSubquery>;
  },
  feedRowPredicate: SQL,
): (SQL | undefined)[] {
  const conditions: (SQL | undefined)[] = [feedRowPredicate];
  const onlyNeverLoggedIn = isOnlyNeverLoggedInFilter(config);

  conditions.push(
    ...tabTeamAndUnactivatedFilterConditions(
      config.tab,
      config.teamAliases,
      onlyNeverLoggedIn,
      config.unactivatedMembershipIds,
      refs.latestItemAlias,
    ),
  );
  if (config.groupIds.length > 0) {
    conditions.push(inArray(feedGroup.id, config.groupIds));
  }
  if (config.statusFilter.length > 0) {
    conditions.push(or(...config.statusFilter.map((f) => statusFilterToCondition(f, config.unactivatedMembershipIds))));
  }
  if (config.dutyStatusFilter.length > 0) {
    conditions.push(inArray(wsMembershipAvailability.mostRecentDutyStatusStatus, config.dutyStatusFilter));
  }
  if (config.search && config.searchTargets.length > 0) {
    const pattern = `%${config.search}%`;
    const clauses: SQL[] = [];
    for (const target of config.searchTargets) {
      const next = searchClausesForTarget(target, pattern, refs);
      if (next != null) clauses.push(next);
    }
    if (clauses.length > 0) conditions.push(or(...clauses));
  }

  return conditions;
}

/**
 * Builds one OR-branch for a search target: either `or(...)` of `ilike` clauses for a core column, or `exists` for a
 * tag value row.
 */
function searchClausesForTarget(
  target: DriverSearchTarget,
  pattern: string,
  refs: {
    latestItemAlias: LatestItemAliasSubquery;
    latestInvitation: ReturnType<typeof latestInvitationSubquery>;
  },
): SQL | null {
  if (target.kind === "tag") {
    return exists(
      db
        .select({ one: sql`1` })
        .from(workspaceMembershipTag)
        .where(
          and(
            eq(workspaceMembershipTag.workspaceMembershipId, workspaceMembership.id),
            eq(workspaceMembershipTag.tagId, target.tagId),
            eq(workspaceMembershipTag.enabled, true),
            ilike(workspaceMembershipTag.tagValue, pattern),
          ),
        ),
    );
  }
  const parts = searchClausesForColumn(target.field, pattern, refs);
  return parts.length === 0 ? null : or(...parts);
}

/**
 * Builds `ilike` clauses for a single core column (may expand to multiple physical columns).
 */
function searchClausesForColumn(
  field: DriverSearchColumn,
  pattern: string,
  refs: {
    latestItemAlias: LatestItemAliasSubquery;
    latestInvitation: ReturnType<typeof latestInvitationSubquery>;
  },
): SQL[] {
  switch (field) {
    case "name":
      return [
        ilike(account.name, pattern),
        ilike(refs.latestInvitation.name, pattern),
        ilike(refs.latestInvitation.email, pattern),
        ilike(refs.latestInvitation.phoneNumber, pattern),
      ];
    case "alias":
      return [ilike(refs.latestItemAlias.alias, pattern)];
    case "group":
      return [ilike(feedGroup.name, pattern)];
    case "location":
      return [
        ilike(wsMembershipAvailability.mostRecentDeviceLocationNearestAddressMunicipality, pattern),
        ilike(wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion, pattern),
        ilike(wsMembershipAvailability.mostRecentDeviceLocationNearestAddressCountry, pattern),
      ];
    case "samsaraUsername":
      return [
        exists(
          db
            .select({ one: sql`1` })
            .from(workspaceMembershipTag)
            .innerJoin(tag, eq(tag.id, workspaceMembershipTag.tagId))
            .where(
              and(
                eq(workspaceMembershipTag.workspaceMembershipId, workspaceMembership.id),
                eq(workspaceMembershipTag.enabled, true),
                eq(tag.enabled, true),
                eq(tag.label, DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL),
                ilike(workspaceMembershipTag.tagValue, pattern),
              ),
            ),
        ),
      ];
    case "samsaraVehicleName":
      return [
        exists(
          db
            .select({ one: sql`1` })
            .from(workspaceMembershipTag)
            .innerJoin(tag, eq(tag.id, workspaceMembershipTag.tagId))
            .where(
              and(
                eq(workspaceMembershipTag.workspaceMembershipId, workspaceMembership.id),
                eq(workspaceMembershipTag.enabled, true),
                eq(tag.enabled, true),
                eq(tag.label, DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL),
                ilike(workspaceMembershipTag.tagValue, pattern),
              ),
            ),
        ),
      ];
  }
}

function rosterSortOrder(sort: DriverSortKey, displayNameExpr: SQL) {
  /** Case-insensitive — raw DESC would rank lowercase above uppercase (e.g. "test" before "William"). */
  const alphaSortKey = sql<string>`LOWER(COALESCE(${displayNameExpr}, ''))`;
  switch (sort) {
    case "alphaAsc":
      return {
        orderBy: [asc(alphaSortKey), asc(workspaceMembership.id)],
        distinctOn: [alphaSortKey, workspaceMembership.id],
      };
    case "alphaDesc":
      return {
        orderBy: [desc(alphaSortKey), asc(workspaceMembership.id)],
        distinctOn: [alphaSortKey, workspaceMembership.id],
      };
    case "most-recent":
    case "unread":
      return {
        orderBy: [asc(displayNameExpr), asc(workspaceMembership.id)],
        distinctOn: [displayNameExpr, workspaceMembership.id],
      };
  }
}

function standardDriverRosterQuery({
  workspaceId,
  membershipId,
  myCurrentWorkspaceMembershipId,
  filter,
}: {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  filter: DriverFilterConfig;
}) {
  const latestItemAlias = aliasSubquery();
  const latestInvitation = latestInvitationSubquery();
  const displayNameExpr = sql<
    string | null
  >`COALESCE(${account.name}, ${latestInvitation.name}, ${latestInvitation.email}, ${latestInvitation.phoneNumber})`;
  const { orderBy, distinctOn } = rosterSortOrder(filter.sort, displayNameExpr);

  const totalAliasCounts = db
    .select({
      aliasMembershipId: feed.aliasMembershipId,
      totalAliasCount: count().as("totalAliasCount"),
    })
    .from(feed)
    .where(eq(feed.isAliasFeed, true))
    .groupBy(feed.aliasMembershipId)
    .as("totalAliasCounts");

  const subscribedAliasCounts = db
    .select({
      aliasMembershipId: feed.aliasMembershipId,
      subscribedAliasCount: count().as("subscribedAliasCount"),
    })
    .from(feed)
    .innerJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .where(eq(feed.isAliasFeed, true))
    .groupBy(feed.aliasMembershipId)
    .as("subscribedAliasCounts");

  const query = db
    .selectDistinctOn(distinctOn, {
      accountId: account.id,
      workspaceMembershipId: workspaceMembership.id,
      name: displayNameExpr.as("name"),
      workspaceId: feed.workspaceId,
      feedId: feed.id,
      alias: latestItemAlias.alias,
      location: sql<
        string | null
      >`NULLIF(CONCAT_WS(', ', NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressMunicipality}, ''), NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion}, '')), '')`.as(
        "location",
      ),
      volume: wsMembershipAvailability.mostRecentVolumeVolume,
      country: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressCountry,
      stateProvince: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion,
      feedPrefId: feedPreference.id,
      totalAliasCount: totalAliasCounts.totalAliasCount,
      subscribedAliasCount: subscribedAliasCounts.subscribedAliasCount,
      group: feedGroup.name,
      groupId: feedGroup.id,
      calculatedAvailability: wsMembershipAvailability.calculatedAvailability,
      offline: wsMembershipAvailability.offline,
      mostRecentHandsFreeStatusEnabled: wsMembershipAvailability.mostRecentHandsFreeStatusEnabled,
      mostRecentHandsFreeStatusUpdatedAt: wsMembershipAvailability.mostRecentHandsFreeStatusUpdatedAt,
      mostRecentVolumeAudible: wsMembershipAvailability.mostRecentVolumeAudible,
      mostRecentDrivingStatus: wsMembershipAvailability.mostRecentDrivingStatus,
      mostRecentDutyStatusStatus: wsMembershipAvailability.mostRecentDutyStatusStatus,
      mostRecentDutyStatusChangedAt: wsMembershipAvailability.mostRecentDutyStatusChangedAt,
      mostRecentDrivingStatusChangedAt: wsMembershipAvailability.mostRecentDrivingStatusChangedAt,
      membershipAvailabilityUpdatedAt: wsMembershipAvailability.updatedAt,
      latitude: wsMembershipAvailability.mostRecentDeviceLocationLatitude,
      longitude: wsMembershipAvailability.mostRecentDeviceLocationLongitude,
    })
    .from(workspaceMembership)
    .leftJoin(account, eq(workspaceMembership.accountId, account.id))
    .leftJoin(latestInvitation, eq(latestInvitation.workspaceMembershipId, workspaceMembership.id))
    .innerJoin(feed, eq(feed.aliasMembershipId, workspaceMembership.id))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, membershipId),
        inArray(permission.name, ["read", "write"]),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItemAlias, eq(latestItemAlias.feedId, feed.id))
    .leftJoin(wsMembershipAvailability, eq(wsMembershipAvailability.workspaceMembershipId, workspaceMembership.id))
    .leftJoin(feedGroupMembership, eq(feedGroupMembership.feedId, feed.id))
    .leftJoin(feedGroup, eq(feedGroup.id, feedGroupMembership.groupId))
    .leftJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .leftJoin(totalAliasCounts, eq(totalAliasCounts.aliasMembershipId, workspaceMembership.id))
    .leftJoin(subscribedAliasCounts, eq(subscribedAliasCounts.aliasMembershipId, workspaceMembership.id))
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        ...buildFilterConditions(filter, { latestItemAlias, latestInvitation }, eq(feed.isAliasFeed, true)),
      ),
    )
    .orderBy(...orderBy);

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Driver Roster Query", query);
  return query;
}

export function driverRosterQuery(params: {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  filter: DriverFilterConfig;
}) {
  if (isOnlyNeverLoggedInFilter(params.filter) && params.filter.unactivatedMembershipIds.length > 0) {
    return unactivatedDriverRosterQuery(params);
  }
  return standardDriverRosterQuery(params);
}

function unactivatedDriverRosterQuery({
  workspaceId,
  membershipId,
  myCurrentWorkspaceMembershipId,
  filter,
}: {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  filter: DriverFilterConfig;
}) {
  const latestItemAlias = aliasSubquery();
  const latestInvitation = latestInvitationSubquery();
  const aliasFeed = primaryAliasFeedSubquery();
  const displayNameExpr = sql<
    string | null
  >`COALESCE(${account.name}, ${latestInvitation.name}, ${latestInvitation.email}, ${latestInvitation.phoneNumber})`;
  const { orderBy, distinctOn } = rosterSortOrder(filter.sort, displayNameExpr);

  const totalAliasCounts = db
    .select({
      aliasMembershipId: feed.aliasMembershipId,
      totalAliasCount: count().as("totalAliasCount"),
    })
    .from(feed)
    .where(eq(feed.isAliasFeed, true))
    .groupBy(feed.aliasMembershipId)
    .as("totalAliasCounts");

  const subscribedAliasCounts = db
    .select({
      aliasMembershipId: feed.aliasMembershipId,
      subscribedAliasCount: count().as("subscribedAliasCount"),
    })
    .from(feed)
    .innerJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .where(eq(feed.isAliasFeed, true))
    .groupBy(feed.aliasMembershipId)
    .as("subscribedAliasCounts");

  const feedRowPredicate = or(isNull(aliasFeed.id), eq(aliasFeed.isAliasFeed, true));

  const query = db
    .selectDistinctOn(distinctOn, {
      accountId: account.id,
      workspaceMembershipId: workspaceMembership.id,
      name: displayNameExpr.as("name"),
      workspaceId: sql<string>`COALESCE(${aliasFeed.workspaceId}, ${workspaceMembership.workspaceId})`.as(
        "workspaceId",
      ),
      feedId: aliasFeed.id,
      alias: latestItemAlias.alias,
      location: sql<
        string | null
      >`NULLIF(CONCAT_WS(', ', NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressMunicipality}, ''), NULLIF(${wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion}, '')), '')`.as(
        "location",
      ),
      volume: wsMembershipAvailability.mostRecentVolumeVolume,
      country: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressCountry,
      stateProvince: wsMembershipAvailability.mostRecentDeviceLocationNearestAddressRegion,
      feedPrefId: feedPreference.id,
      totalAliasCount: totalAliasCounts.totalAliasCount,
      subscribedAliasCount: subscribedAliasCounts.subscribedAliasCount,
      group: feedGroup.name,
      groupId: feedGroup.id,
      calculatedAvailability: wsMembershipAvailability.calculatedAvailability,
      offline: wsMembershipAvailability.offline,
      mostRecentHandsFreeStatusEnabled: wsMembershipAvailability.mostRecentHandsFreeStatusEnabled,
      mostRecentHandsFreeStatusUpdatedAt: wsMembershipAvailability.mostRecentHandsFreeStatusUpdatedAt,
      mostRecentVolumeAudible: wsMembershipAvailability.mostRecentVolumeAudible,
      mostRecentDrivingStatus: wsMembershipAvailability.mostRecentDrivingStatus,
      mostRecentDutyStatusStatus: wsMembershipAvailability.mostRecentDutyStatusStatus,
      mostRecentDutyStatusChangedAt: wsMembershipAvailability.mostRecentDutyStatusChangedAt,
      mostRecentDrivingStatusChangedAt: wsMembershipAvailability.mostRecentDrivingStatusChangedAt,
      membershipAvailabilityUpdatedAt: wsMembershipAvailability.updatedAt,
      latitude: wsMembershipAvailability.mostRecentDeviceLocationLatitude,
      longitude: wsMembershipAvailability.mostRecentDeviceLocationLongitude,
    })
    .from(workspaceMembership)
    .leftJoin(account, eq(workspaceMembership.accountId, account.id))
    .leftJoin(latestInvitation, eq(latestInvitation.workspaceMembershipId, workspaceMembership.id))
    .leftJoin(aliasFeed, eq(aliasFeed.aliasMembershipId, workspaceMembership.id))
    .leftJoin(
      permission,
      and(
        eq(permission.feedId, aliasFeed.id),
        eq(permission.workspaceMembershipId, membershipId),
        inArray(permission.name, ["read", "write"]),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItemAlias, eq(latestItemAlias.feedId, aliasFeed.id))
    .leftJoin(wsMembershipAvailability, eq(wsMembershipAvailability.workspaceMembershipId, workspaceMembership.id))
    .leftJoin(feedGroupMembership, eq(feedGroupMembership.feedId, aliasFeed.id))
    .leftJoin(feedGroup, eq(feedGroup.id, feedGroupMembership.groupId))
    .leftJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, aliasFeed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .leftJoin(totalAliasCounts, eq(totalAliasCounts.aliasMembershipId, workspaceMembership.id))
    .leftJoin(subscribedAliasCounts, eq(subscribedAliasCounts.aliasMembershipId, workspaceMembership.id))
    .where(
      and(
        eq(workspaceMembership.workspaceId, workspaceId),
        eq(workspaceMembership.status, "active"),
        inArray(workspaceMembership.id, filter.unactivatedMembershipIds),
        or(isNull(aliasFeed.id), isNotNull(permission.id)),
        ...buildFilterConditions(filter, { latestItemAlias, latestInvitation }, feedRowPredicate),
      ),
    )
    .orderBy(...orderBy);

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as ReturnType<typeof standardDriverRosterQuery>;
  }
  logQuery("Driver Roster Query (unactivated)", query);
  return query as unknown as ReturnType<typeof standardDriverRosterQuery>;
}

export function driverVolatileQuery({
  workspaceId,
  membershipId,
  language,
}: {
  workspaceId: string;
  membershipId: string;
  language: string;
}) {
  const latestItem = db
    .selectDistinctOn([item.feedId], {
      feedId: item.feedId,
      contentId: item.contentId,
    })
    .from(item)
    .innerJoin(feed, and(eq(feed.id, item.feedId), eq(feed.isAliasFeed, true)))
    .where(and(isNull(item.deletedAt), eq(item.isFromAliasMember, true)))
    .orderBy(item.feedId, desc(item.createdAt))
    .as("latestItem");

  const hasUnread = db
    .select({
      feedId: item.feedId,
      hasUnread: sql<boolean>`bool_or(${item.unread})`.as("hasUnread"),
    })
    .from(item)
    .innerJoin(feed, and(eq(feed.id, item.feedId), eq(feed.isAliasFeed, true)))
    .where(isNull(item.deletedAt))
    .groupBy(item.feedId)
    .as("hasUnread");

  const query = db
    .selectDistinctOn([feed.id], {
      feedId: feed.id,
      aliasMembershipId: feed.aliasMembershipId,
      recentMessage: sql<string | null>`COALESCE(${transcription.transcriptionContent}, ${file.name})`.as(
        "recentMessage",
      ),
      unread: hasUnread.hasUnread,
      latestItemCreatedAt: feed.latestAliasActivity,
    })
    .from(feed)
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, membershipId),
        inArray(permission.name, ["read", "write"]),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItem, eq(latestItem.feedId, feed.id))
    .leftJoin(hasUnread, eq(hasUnread.feedId, feed.id))
    .leftJoin(
      transcription,
      and(eq(transcription.contentId, latestItem.contentId), eq(transcription.language, language)),
    )
    .leftJoin(file, eq(file.contentId, latestItem.contentId))
    .where(and(eq(feed.workspaceId, workspaceId), eq(feed.isAliasFeed, true)));

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Driver Volatile Query", query);
  return query;
}

export function myDriverUnreadCountQuery({
  workspaceId,
  membershipId,
  teamAliases,
}: {
  workspaceId: string;
  membershipId: string;
  teamAliases: string[];
}) {
  const latestItemAlias = aliasSubquery();
  const hasUnread = db
    .select({
      feedId: item.feedId,
      hasUnread: sql<boolean>`bool_or(${item.unread})`.as("hasUnread"),
    })
    .from(item)
    .innerJoin(feed, and(eq(feed.id, item.feedId), eq(feed.isAliasFeed, true)))
    .where(isNull(item.deletedAt))
    .groupBy(item.feedId)
    .as("hasUnread");

  const conditions: (SQL | undefined)[] = [
    eq(workspaceMembership.workspaceId, workspaceId),
    eq(workspaceMembership.status, "active"),
    isNotNull(feedPreference.id),
    eq(hasUnread.hasUnread, true),
  ];
  if (teamAliases.length > 0) {
    conditions.push(inArray(latestItemAlias.alias, teamAliases));
  }

  const query = db
    .select({ count: countDistinct(workspaceMembership.id) })
    .from(workspaceMembership)
    .innerJoin(feed, and(eq(feed.aliasMembershipId, workspaceMembership.id), eq(feed.isAliasFeed, true)))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, membershipId),
        inArray(permission.name, ["read", "write"]),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItemAlias, eq(latestItemAlias.feedId, feed.id))
    .leftJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, membershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .innerJoin(hasUnread, eq(hasUnread.feedId, feed.id))
    .where(and(...conditions));

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("My Driver Unread Count Query", query);
  return query;
}

export function driverTotalCountQuery({
  workspaceId,
  membershipId,
  tab,
  teamAliases,
}: {
  workspaceId: string;
  membershipId: string;
  tab: DriverViewTab;
  teamAliases: string[];
}) {
  const latestItemAlias = aliasSubquery();

  const conditions: (SQL | undefined)[] = [
    eq(workspaceMembership.workspaceId, workspaceId),
    eq(workspaceMembership.status, "active"),
  ];

  if (tab === "my") {
    conditions.push(isNotNull(feedPreference.id));
    if (teamAliases.length > 0) {
      conditions.push(inArray(latestItemAlias.alias, teamAliases));
    }
  }
  if (tab === "all" && teamAliases.length > 0) {
    conditions.push(inArray(latestItemAlias.alias, teamAliases));
  }

  const query = db
    .select({ count: countDistinct(workspaceMembership.id) })
    .from(workspaceMembership)
    .innerJoin(feed, and(eq(feed.aliasMembershipId, workspaceMembership.id), eq(feed.isAliasFeed, true)))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.workspaceMembershipId, membershipId),
        inArray(permission.name, ["read", "write"]),
        eq(permission.enabled, true),
      ),
    )
    .leftJoin(latestItemAlias, eq(latestItemAlias.feedId, feed.id))
    .leftJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, membershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    )
    .where(and(...conditions));

  if (!workspaceId || !membershipId) {
    return emptyQuery as unknown as typeof query;
  }
  logQuery("Driver Total Count Query", query);
  return query;
}
